'use client'

import * as React from 'react'
import { createPortal } from 'react-dom'
import Confetti from 'react-confetti'
import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Crown, Users, Coins, Tag, PartyPopper, Check, Rocket, Loader2 } from 'lucide-react'

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { useToast } from '@/components/ui/use-toast'

import { ProductSelector } from '@/components/form/product-selector'
import { PromotionActionsSection } from '@/components/form/discount/promotion-actions-section'
import type { Action, PromotionFormData } from '@/components/form/discount/promotion-form'
import {
    buildExtraMountNode,
    funnelHasClubStep,
    addVipBumpToCheckout,
    EXTRA_MOUNTS,
} from '@/components/funnels/buildWizardActions'
import { ClubOfferForm, type ClubOfferFormHandle } from './ClubOfferForm'

import {
    useClubByStoreId,
    useCreateClub,
    useUpdateClub,
    useDefineMembershipProductTrigger,
} from '@/lib/api/clubs'
import { useStore, useUpdateStoreCreditSettings } from '@/lib/api/stores'
import { useCreatePromotion, useStorePromotions } from '@/lib/api/promotions'
import { useCreateUpsell, useUpdateUpsell } from '@/lib/api/upsells'
import { useFunnelsList, useUpdateFunnelWithRouting } from '@/lib/api/funnels'
import { trpc } from '@/lib/api/trpc'
import { isVipDiscount, getActionLabels } from '@/lib/promotion-discount'

import { CreateProductDialog } from './CreateProductDialog'

type ActionType =
    | 'LineItemAdjustment'
    | 'OrderAdjustment'
    | 'ShippingAdjustment'
    | 'CreateLineItem'
    | 'SubscriptionLineItemFreeTrial'
    | 'BOGO'

type StepKey = 'welcome' | 'product' | 'creditsAsk' | 'creditsSetup' | 'offer' | 'funnels' | 'done'

/** A store funnel that has no /club mount step yet — a retrofit candidate. */
interface ClublessFunnel {
    id: string
    name: string
    config: { nodes?: any[] } & Record<string, any>
}

interface ClubSetupWizardProps {
    storeId: string
    open: boolean
    onOpenChange: (open: boolean) => void
}

const useWindowSize = () => {
    const [size, setSize] = React.useState({ width: 0, height: 0 })
    React.useEffect(() => {
        const update = () => setSize({ width: window.innerWidth, height: window.innerHeight })
        update()
        window.addEventListener('resize', update)
        return () => window.removeEventListener('resize', update)
    }, [])
    return size
}

export const ClubSetupWizard = ({ storeId, open, onOpenChange }: ClubSetupWizardProps) => {
    const { t } = useTranslation('club')
    const { toast } = useToast()
    const { width, height } = useWindowSize()

    const { data: club } = useClubByStoreId({ storeId }, !!storeId && open)
    const { data: store } = useStore(storeId)
    const currency = store?.baseCurrency || 'USD'

    const createClub = useCreateClub()
    const updateClub = useUpdateClub()
    const defineTrigger = useDefineMembershipProductTrigger()
    const updateCreditSettings = useUpdateStoreCreditSettings()
    const createPromotion = useCreatePromotion()
    const createUpsell = useCreateUpsell()
    const updateUpsell = useUpdateUpsell()
    const updateFunnelWithRouting = useUpdateFunnelWithRouting()
    const utils = trpc.useUtils()

    // Existing VIP discounts for this club's product. A discount is "VIP" purely
    // by its conditioning (isVipDiscount) — there's no selection here, we just
    // surface any that already qualify so the merchant doesn't recreate one.
    const { data: promoData } = useStorePromotions({ storeId, pageSize: 100 }, !!storeId && open)
    const vipDiscounts = (promoData?.promotions ?? []).filter((p) => isVipDiscount(p, club?.productId))

    // Funnels missing a /club step (Step "funnels"). Scanned lazily on entry.
    const { data: funnelsData } = useFunnelsList(storeId)
    const [funnelScan, setFunnelScan] = React.useState<{ loading: boolean; missing: ClublessFunnel[] }>({
        loading: false,
        missing: [],
    })
    const [selectedFunnelIds, setSelectedFunnelIds] = React.useState<Set<string>>(new Set())

    const [stepKey, setStepKey] = React.useState<StepKey>('welcome')
    const [createProductOpen, setCreateProductOpen] = React.useState(false)
    const [offerCreated, setOfferCreated] = React.useState(false)
    const [wantOffer, setWantOffer] = React.useState<boolean | null>(null)
    const offerFormRef = React.useRef<ClubOfferFormHandle>(null)
    const [offerSubmitState, setOfferSubmitState] = React.useState({ canSubmit: false, isSubmitting: false })

    // Credits
    const [wantCredits, setWantCredits] = React.useState<boolean | null>(null)
    const [creditsPerRebill, setCreditsPerRebill] = React.useState(100)
    const [rewardEnabled, setRewardEnabled] = React.useState(false)
    const [rewardCredits, setRewardCredits] = React.useState(100)
    const [rewardAmount, setRewardAmount] = React.useState(10)
    const [redeemCredits, setRedeemCredits] = React.useState(100)
    const [redeemAmount, setRedeemAmount] = React.useState(1)

    // Live example helpers (mirror the Credits tab)
    const exampleSpend = 50
    const fmt = (amount: number) => `${amount.toFixed(2)} ${currency}`
    const valueOfCredits = (credits: number) => (redeemCredits > 0 ? (credits / redeemCredits) * redeemAmount : 0)
    const earnedFromOrder =
        rewardEnabled && rewardAmount > 0 ? Math.round(rewardCredits * (exampleSpend / rewardAmount)) : 0
    const coin = <Coins className="-mt-0.5 inline h-4 w-4 text-amber-500" />

    // VIP discount actions (reuses the discount actions editor)
    const discountForm = useForm<PromotionFormData>({
        defaultValues: {
            name: '',
            enabled: true,
            automatic: true,
            usageLimit: undefined,
            startDate: new Date(),
            endDate: null,
            ruleOperator: 'OR',
            combinesWithOrderLevelDiscounts: true,
            combinesWithLineItemDiscounts: true,
            combinesWithShippingDiscounts: true,
            forceCombine: true,
            oncePerCustomer: false,
            rules: [],
            actions: [],
        },
    })
    const discountName = discountForm.watch('name')
    const discountActions = discountForm.watch('actions')
    // A discount with actions must be named before we can create it; an empty
    // builder (no actions) is fine — the merchant just skips the discount.
    const discountNeedsName = discountActions.length > 0 && discountName.trim().length === 0

    const addAction = (type: ActionType) => {
        const a: Action = { type }
        if (type === 'LineItemAdjustment' || type === 'OrderAdjustment') {
            a.adjustmentType = 'percentage'
            a.adjustmentPercentage = 10
        } else if (type === 'ShippingAdjustment') {
            a.freeShipping = true
        } else if (type === 'SubscriptionLineItemFreeTrial') {
            a.subscriptionFreeTrialDuration = 7
            a.subscriptionFreeTrialDurationType = 'day'
        } else if (type === 'BOGO') {
            a.buyQuantityThreshold = 1
            a.quantityToAdd = 1
        }
        discountForm.setValue('actions', [...discountForm.getValues('actions'), a])
    }
    const updateAction = (i: number, updates: Partial<Action>) => {
        const next = [...discountForm.getValues('actions')]
        next[i] = { ...next[i], ...updates }
        discountForm.setValue('actions', next)
    }
    const removeAction = (i: number) =>
        discountForm.setValue(
            'actions',
            discountForm.getValues('actions').filter((_, idx) => idx !== i),
        )

    const ensureClubId = async (): Promise<string | null> => {
        if (club?.id) return club.id
        if (!storeId) return null
        const [created] = await createClub.mutateAsync({ storeId, active: false })
        return created.clubId
    }

    const handleProductChange = async (productId: string) => {
        const clubId = await ensureClubId()
        if (!clubId) return
        defineTrigger.mutate({ clubId, storeId, productId: productId || null })
    }

    // Dynamic step order (credit setup only when chosen).
    const steps: StepKey[] = [
        'welcome',
        'product',
        'creditsAsk',
        ...(wantCredits ? (['creditsSetup'] as StepKey[]) : []),
        'offer',
        'funnels',
        'done',
    ]
    const stepIndex = Math.max(0, steps.indexOf(stepKey))

    // Stable key for the funnel set — useFunnelsList returns a fresh wrapper
    // object every render, so we depend on the id list (a primitive) to avoid
    // re-running the scan (and re-firing getById) on every render.
    const funnelIdsKey = (funnelsData?.funnels ?? []).map((f) => f.id).join(',')

    // Scan the store's funnels for missing /club steps when the funnels step
    // opens. The list endpoint omits node config, so we fetch each funnel's
    // full config to detect the mount point precisely.
    React.useEffect(() => {
        if (stepKey !== 'funnels' || !storeId) return
        const list = funnelsData?.funnels ?? []
        let cancelled = false
        setFunnelScan({ loading: true, missing: [] })
        ;(async () => {
            const missing: ClublessFunnel[] = []
            for (const f of list) {
                try {
                    const full = await utils.funnels.getById.fetch({ funnelId: f.id, storeId })
                    if (full?.config && !funnelHasClubStep(full.config)) {
                        missing.push({ id: f.id, name: f.name, config: full.config })
                    }
                } catch {
                    // Skip funnels we can't read; they just won't be offered.
                }
            }
            if (!cancelled) {
                setFunnelScan({ loading: false, missing })
                setSelectedFunnelIds(new Set(missing.map((m) => m.id)))
            }
        })()
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepKey, storeId, funnelIdsKey])

    const reset = () => {
        setStepKey('welcome')
        setWantCredits(null)
        setOfferCreated(false)
        setWantOffer(null)
        setFunnelScan({ loading: false, missing: [] })
        setSelectedFunnelIds(new Set())
        discountForm.reset()
    }

    const handleOpenChange = (next: boolean) => {
        if (!next) reset()
        onOpenChange(next)
    }

    // Create a fresh VIP discount from the builder. No-op when the builder is
    // empty (no name / no actions) — that's a valid "skip the discount" path.
    // Existing VIP discounts are never touched here; they're only listed.
    const createVipDiscountIfAny = async () => {
        const name = discountForm.getValues('name').trim()
        const actions = discountForm.getValues('actions')
        if (!name || actions.length === 0 || !club?.productId) return
        await createPromotion.mutateAsync({
            storeId,
            name,
            enabled: true,
            automatic: true,
            usageLimit: null,
            oncePerCustomer: false,
            forceCombine: true,
            combinesWithOrderLevelDiscounts: true,
            combinesWithLineItemDiscounts: true,
            combinesWithShippingDiscounts: true,
            ruleOperator: 'OR' as const,
            rules: [{ type: 'ContainsProduct', productId: club.productId }],
            actions: actions.map((a) => ({ ...a })),
        } as any)
    }

    // Ensure a single store-level VIP order bump exists for the club product and
    // return its offer id (referenced by checkout stepConfig.enabledOfferIds) plus
    // the ids of all currently-enabled bump offers (used to preserve inherited
    // bumps when a checkout step is switched to custom mode).
    //
    // The bump sells the club/VIP product (default variant + price). Its real
    // discount rides the automatic VIP promotion (rule: ContainsProduct = club
    // product) the wizard already creates; compareAtPriceDiscount only drives the
    // strikethrough display. An existing VIP bump is updated in place (id kept)
    // rather than duplicated.
    const ensureVipBump = async (): Promise<{ vipOfferId: string | null; enabledBumpOfferIds: string[] }> => {
        const empty = { vipOfferId: null as string | null, enabledBumpOfferIds: [] as string[] }
        if (!storeId || !club?.productId) return empty

        // Resolve a variant + price for the club product. Prefer the default
        // variant, but fall back to any variant that actually has a usable price
        // (a priceless default variant shouldn't block a sibling that has one).
        const product = await utils.products.getById.fetch({ productId: club.productId })
        const variants = ((product as any)?.variants ?? []) as any[]
        if (variants.length === 0) throw new Error(t('setup.funnels.vipBumpNoVariant'))
        const ordered = [variants.find((v) => v.default), ...variants].filter(Boolean) as any[]
        let variant: any = null
        let price: any = null
        for (const v of ordered) {
            const ps = (v.prices ?? []) as any[]
            const p =
                ps.find((x) => x.default && !x.recurring) ?? ps.find((x) => !x.recurring) ?? ps[0]
            if (p) {
                variant = v
                price = p
                break
            }
        }
        if (!variant || !price) throw new Error(t('setup.funnels.vipBumpNoPrice'))

        // VIP discount % for the display strikethrough (real discount = automatic promo).
        const pctAction = discountForm
            .getValues('actions')
            .find(
                (a) =>
                    (a.type === 'OrderAdjustment' || a.type === 'LineItemAdjustment') &&
                    a.adjustmentType === 'percentage',
            )
        const discountPct = Math.min(100, Math.max(0, Math.round(pctAction?.adjustmentPercentage ?? 0)))

        const offerPayload = {
            productId: club.productId,
            variantId: variant.id as string,
            priceId: price.id as string,
            type: 'vip' as const,
            precheck: true,
            quantity: 1,
            titleTrans: { en: ((product as any)?.name as string) || 'VIP membership' },
            descriptionTrans: {},
            overrideImageUrl: null,
            displayPrice: true,
            displayCompareAtPrice: discountPct > 0,
            compareAtPriceDiscount: discountPct > 0 ? discountPct : 20,
            rules: [],
        }

        // Replace an existing VIP bump if there is one; otherwise create a new one.
        const list = await utils.upsells.list.fetch({ storeId, type: 'orderbump' })
        const upsells = ((list as any)?.upsells ?? []) as any[]
        const vipUpsell = upsells.find((u) => (u.orderBumpOffers ?? []).some((o: any) => o.type === 'vip'))

        let vipOfferId: string | null = null
        if (vipUpsell) {
            const vipOffer = (vipUpsell.orderBumpOffers ?? []).find((o: any) => o.type === 'vip')
            vipOfferId = vipOffer?.id ?? null
            // Keep sibling offers intact; only swap the VIP one (id preserved).
            const orderBumpOffers = (vipUpsell.orderBumpOffers ?? []).map((o: any) =>
                o.type === 'vip'
                    ? { id: o.id, ...offerPayload }
                    : {
                          id: o.id,
                          productId: o.productId,
                          variantId: o.variantId ?? null,
                          priceId: o.priceId ?? null,
                          type: o.type,
                          precheck: !!o.precheck,
                          quantity: o.quantity ?? 1,
                          titleTrans: o.titleTrans ?? {},
                          descriptionTrans: o.descriptionTrans ?? {},
                          overrideImageUrl: o.overrideImageUrl ?? null,
                          displayPrice: o.displayPrice ?? true,
                          displayCompareAtPrice: o.displayCompareAtPrice ?? false,
                          compareAtPriceDiscount: o.compareAtPriceDiscount ?? 20,
                          rules: o.rules ?? [],
                      },
            )
            await updateUpsell.mutateAsync({
                id: vipUpsell.id,
                data: {
                    offerTitle: vipUpsell.offerTitle || 'VIP membership',
                    enabled: true,
                    type: 'orderbump',
                    triggers: [{ type: 'any', productId: null }],
                    offers: [],
                    orderBumpOffers,
                },
            } as any)
        } else {
            const created = await createUpsell.mutateAsync({
                storeId,
                offerTitle: 'VIP membership',
                enabled: true,
                type: 'orderbump',
                triggers: [{ type: 'any', productId: null }],
                offers: [],
                orderBumpOffers: [offerPayload],
            } as any)
            // create returns only the upsell id; refetch to read the new offer id.
            const full = await utils.upsells.getById.fetch({ id: created.id })
            const vipOffer = ((full as any)?.orderBumpOffers ?? []).find((o: any) => o.type === 'vip')
            vipOfferId = vipOffer?.id ?? null
        }

        // Re-read so a freshly created VIP bump is included in the enabled set.
        const after = await utils.upsells.list.fetch({ storeId, type: 'orderbump' })
        const enabledBumpOfferIds = (((after as any)?.upsells ?? []) as any[])
            .filter((u) => u.enabled)
            .flatMap((u) => (u.orderBumpOffers ?? []).map((o: any) => o.id as string))

        return { vipOfferId, enabledBumpOfferIds }
    }

    // Append a /club mount step to each selected funnel that lacks one, and link
    // the VIP order bump to that funnel's checkout step (if it has one). The
    // backend fills the node's instanceId on save, so no extra deploy is needed.
    const deployClubToFunnels = async () => {
        const targets = funnelScan.missing.filter((m) => selectedFunnelIds.has(m.id))
        const clubMount = EXTRA_MOUNTS.find((m) => m.mountPoint === 'club')
        if (!clubMount) return

        // One store-level VIP bump, created/refreshed once, linked to each checkout.
        // Surface failures (no price/variant, API error) instead of silently skipping.
        let vipOfferId: string | null = null
        let enabledBumpOfferIds: string[] = []
        try {
            const r = await ensureVipBump()
            vipOfferId = r.vipOfferId
            enabledBumpOfferIds = r.enabledBumpOfferIds
        } catch (e) {
            toast({
                title: t('setup.funnels.vipBumpErrorTitle'),
                description: e instanceof Error ? e.message : t('setup.funnels.vipBumpErrorDesc'),
                variant: 'destructive',
            })
        }

        for (const target of targets) {
            let cfg: any = { ...target.config, name: target.config.name ?? target.name }
            if (vipOfferId) cfg = addVipBumpToCheckout(cfg, vipOfferId, enabledBumpOfferIds)
            const nodes = cfg.nodes ?? []
            const checkout = nodes.find((n: any) => n.type === 'checkout')
            const baseX = checkout?.position?.x ?? 0
            const baseY = checkout?.position?.y ?? 0
            const clubNode = buildExtraMountNode(clubMount, baseX, baseY, 0)
            await updateFunnelWithRouting.mutateAsync({
                funnelId: target.id,
                storeId,
                config: {
                    ...cfg,
                    nodes: [...nodes, clubNode],
                },
            } as any)
        }
    }

    const saveCredits = async () => {
        await updateCreditSettings.mutateAsync({
            storeId,
            enabled: true,
            defaultCreditsPerRebill: creditsPerRebill,
            creditRatio: { credits: redeemCredits, baseAmount: Math.round(redeemAmount * 100) },
            creditRewardRatio: {
                enabled: rewardEnabled,
                credits: rewardCredits,
                baseAmount: Math.round(rewardAmount * 100),
            },
        })
    }

    const goNext = () => {
        // Fire the step's save in the background and advance immediately.
        // Mutation hooks surface their own error toasts on failure.
        if (stepKey === 'product') {
            void createVipDiscountIfAny().catch(() => {})
        }
        if (stepKey === 'creditsSetup') {
            void saveCredits().catch(() => {})
        }
        if (stepKey === 'offer') {
            // Activating finishes setup.
            if (club?.id) void updateClub.mutateAsync({ clubId: club.id, data: { active: true } }).catch(() => {})
        }
        if (stepKey === 'funnels') {
            void deployClubToFunnels().catch(() => {})
        }
        const idx = steps.indexOf(stepKey)
        const next = steps[idx + 1]
        if (next) setStepKey(next)
    }

    const goBack = () => {
        const idx = steps.indexOf(stepKey)
        const prev = steps[idx - 1]
        if (prev) setStepKey(prev)
    }

    const finish = () => {
        toast({ title: t('setup.toasts.doneTitle'), description: t('setup.toasts.doneDesc') })
        handleOpenChange(false)
    }

    const canNext =
        stepKey === 'welcome'
            ? true
            : stepKey === 'product'
              ? !!club?.productId && !discountNeedsName
              : stepKey === 'creditsAsk'
                ? wantCredits !== null
                : true

    // When the offer form is showing, the footer drives its Cancel / Create offer.
    const offerFormActive = stepKey === 'offer' && !!wantOffer && !offerCreated

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="w-full sm:max-w-3xl">
                {stepKey === 'done' &&
                    width > 0 &&
                    createPortal(
                        <div className="pointer-events-none fixed inset-0 z-[200] overflow-hidden">
                            <Confetti
                                width={width}
                                height={height}
                                recycle={false}
                                numberOfPieces={400}
                                tweenDuration={6000}
                            />
                        </div>,
                        document.body,
                    )}

                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Crown className="h-5 w-5 text-amber-500" />
                        {t('setup.title')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('setup.stepCount', { current: stepIndex + 1, total: steps.length })}
                    </DialogDescription>
                </DialogHeader>

                {/* Progress */}
                <div className="flex gap-1.5">
                    {steps.map((s, i) => (
                        <div
                            key={s}
                            className={`h-1.5 flex-1 rounded-full ${i <= stepIndex ? 'bg-amber-500' : 'bg-muted'}`}
                        />
                    ))}
                </div>

                <div className="max-h-[60vh] space-y-4 overflow-y-auto py-2">
                    {stepKey === 'welcome' && (
                        <div className="space-y-4 py-4 text-center">
                            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400">
                                <Crown className="h-8 w-8" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold">{t('setup.welcome.title')}</h3>
                                <p className="text-muted-foreground mx-auto mt-1 max-w-md text-sm">
                                    {t('setup.welcome.description')}
                                </p>
                            </div>
                        </div>
                    )}

                    {stepKey === 'product' && (
                        <div className="space-y-5">
                            <div>
                                <h3 className="flex items-center gap-2 font-semibold">
                                    <Users className="h-4 w-4" /> {t('setup.product.title')}
                                </h3>
                                <p className="text-muted-foreground text-sm">{t('setup.product.description')}</p>
                            </div>
                            <ProductSelector
                                storeId={storeId}
                                productId={club?.productId || undefined}
                                onProductChange={handleProductChange}
                                label={t('config.membershipProduct.label')}
                                action={
                                    <Button type="button" variant="outline" onClick={() => setCreateProductOpen(true)}>
                                        {t('config.membershipProduct.createProduct')}
                                    </Button>
                                }
                            />

                            <div className="space-y-3 border-t pt-4">
                                <div>
                                    <h4 className="flex items-center gap-2 text-sm font-semibold">
                                        <Tag className="h-4 w-4" /> {t('setup.discount.title')}
                                    </h4>
                                    <p className="text-muted-foreground text-xs">{t('setup.discount.description')}</p>
                                </div>

                                {/* Existing VIP discounts — listed read-only. A discount is "VIP"
                                    purely by its conditioning, so there's nothing to select here. */}
                                {vipDiscounts.length > 0 && (
                                    <div className="space-y-2">
                                        <p className="text-muted-foreground text-xs">{t('setup.discount.existing')}</p>
                                        {vipDiscounts.map((p) => (
                                            <div
                                                key={p.id}
                                                className="flex items-center gap-2 rounded-lg border bg-muted/50 p-2.5 text-sm"
                                            >
                                                <Tag className="text-primary h-4 w-4 flex-shrink-0" />
                                                <span className="min-w-0 flex-1 truncate font-medium">{p.name}</span>
                                                <div className="flex flex-shrink-0 items-center gap-1">
                                                    {getActionLabels(p.actions, currency).map((l, i) => (
                                                        <span
                                                            key={i}
                                                            className="rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900 dark:text-amber-300"
                                                        >
                                                            {l}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="space-y-1">
                                    <Label htmlFor="wizard-discount-name">{t('vipDiscount.name')}</Label>
                                    <Input
                                        id="wizard-discount-name"
                                        value={discountName}
                                        onChange={(e) => discountForm.setValue('name', e.target.value)}
                                        placeholder={t('vipDiscount.namePlaceholder')}
                                        disabled={!club?.productId}
                                    />
                                    {discountNeedsName && (
                                        <p className="text-destructive text-xs">{t('setup.discount.nameRequired')}</p>
                                    )}
                                </div>
                                {club?.productId && (
                                    <FormProvider {...discountForm}>
                                        <PromotionActionsSection
                                            storeId={storeId}
                                            onAddAction={addAction}
                                            onUpdateAction={updateAction}
                                            onRemoveAction={removeAction}
                                            hideAdvancedTargeting
                                        />
                                    </FormProvider>
                                )}
                            </div>
                        </div>
                    )}

                    {stepKey === 'creditsAsk' && (
                        <div className="space-y-4">
                            <div className="text-center">
                                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400">
                                    <Coins className="h-7 w-7" />
                                </div>
                                <h3 className="mt-2 font-semibold">{t('setup.creditsAsk.title')}</h3>
                                <p className="text-muted-foreground text-sm">{t('setup.creditsAsk.description')}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => setWantCredits(true)}
                                    className={`rounded-xl border-2 p-4 text-center transition ${wantCredits === true ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'hover:bg-accent'}`}
                                >
                                    <p className="font-medium">{t('setup.creditsAsk.yes')}</p>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setWantCredits(false)}
                                    className={`rounded-xl border-2 p-4 text-center transition ${wantCredits === false ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'hover:bg-accent'}`}
                                >
                                    <p className="font-medium">{t('setup.creditsAsk.no')}</p>
                                </button>
                            </div>
                        </div>
                    )}

                    {stepKey === 'creditsSetup' && (
                        <div className="space-y-4">
                            <div>
                                <h3 className="flex items-center gap-2 font-semibold">
                                    <Coins className="h-4 w-4" /> {t('setup.creditsSetup.title')}
                                </h3>
                                <p className="text-muted-foreground text-sm">{t('setup.creditsSetup.description')}</p>
                            </div>
                            {/* Earning */}
                            <section className="space-y-3">
                                <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                                    {t('creditSettings.fun.earning')}
                                </p>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
                                    <span>{t('creditSettings.fun.rebillBefore')}</span>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={creditsPerRebill}
                                        onChange={(e) => setCreditsPerRebill(Number(e.target.value))}
                                        className="h-7 w-16 px-2 text-center text-sm font-semibold [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                    />
                                    <span className="font-medium">
                                        <Coins className="-mt-0.5 inline h-4 w-4 text-amber-500" /> {t('creditSettings.fun.credits')}
                                    </span>
                                </div>
                                <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 text-sm ${rewardEnabled ? '' : 'opacity-60'}`}>
                                    <Switch checked={rewardEnabled} onCheckedChange={setRewardEnabled} />
                                    <span>{t('creditSettings.fun.rewardBefore')}</span>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={rewardCredits}
                                        disabled={!rewardEnabled}
                                        onChange={(e) => setRewardCredits(Number(e.target.value))}
                                        className="h-7 w-16 px-2 text-center text-sm font-semibold [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                    />
                                    <span className="font-medium">
                                        <Coins className="-mt-0.5 inline h-4 w-4 text-amber-500" /> {t('creditSettings.fun.rewardMid')}
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                        <Input
                                            type="number"
                                            min="0.01"
                                            step="0.01"
                                            value={rewardAmount}
                                            disabled={!rewardEnabled}
                                            onChange={(e) => setRewardAmount(Number(e.target.value))}
                                            className="h-7 w-16 px-2 text-center text-sm font-semibold [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                        />
                                        <span className="text-muted-foreground text-xs font-medium">{currency}</span>
                                    </span>
                                    <span>{t('creditSettings.fun.rewardAfter')}</span>
                                </div>
                            </section>

                            {/* Redeeming */}
                            <section className="space-y-3">
                                <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                                    {t('creditSettings.fun.redeeming')}
                                </p>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
                                    <Input
                                        type="number"
                                        min="1"
                                        value={redeemCredits}
                                        onChange={(e) => setRedeemCredits(Number(e.target.value))}
                                        className="h-7 w-16 px-2 text-center text-sm font-semibold [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                    />
                                    <span className="font-medium">
                                        <Coins className="-mt-0.5 inline h-4 w-4 text-amber-500" /> {t('creditSettings.fun.credits')}
                                    </span>
                                    <span className="text-muted-foreground text-lg font-medium">=</span>
                                    <span className="inline-flex items-center gap-1">
                                        <Input
                                            type="number"
                                            min="0.01"
                                            step="0.01"
                                            value={redeemAmount}
                                            onChange={(e) => setRedeemAmount(Number(e.target.value))}
                                            className="h-7 w-16 px-2 text-center text-sm font-semibold [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                        />
                                        <span className="text-muted-foreground text-xs font-medium">{currency}</span>
                                    </span>
                                    <span>{t('creditSettings.fun.offAtCheckout')}</span>
                                </div>
                            </section>

                            {/* Live example */}
                            <section className="rounded-xl border bg-muted/40 p-4">
                                <p className="text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wide">
                                    {t('creditSettings.fun.example')}
                                </p>
                                <ul className="space-y-1 text-sm">
                                    <li>
                                        {t('creditSettings.fun.exampleRebill')}:{' '}
                                        <span className="font-semibold">+{creditsPerRebill}</span> {coin} ≈{' '}
                                        <span className="font-semibold">{fmt(valueOfCredits(creditsPerRebill))}</span>
                                    </li>
                                    {rewardEnabled && (
                                        <li>
                                            {t('creditSettings.fun.exampleOrder', {
                                                amount: fmt(exampleSpend),
                                            })}
                                            : <span className="font-semibold">+{earnedFromOrder}</span> {coin} ≈{' '}
                                            <span className="font-semibold">{fmt(valueOfCredits(earnedFromOrder))}</span>
                                        </li>
                                    )}
                                </ul>
                            </section>
                        </div>
                    )}

                    {stepKey === 'offer' && (
                        <div className="space-y-4">
                            <div>
                                <h3 className="flex items-center gap-2 font-semibold">
                                    <Tag className="h-4 w-4" /> {t('setup.offer.title')}
                                </h3>
                                <p className="text-muted-foreground text-sm">{t('setup.offer.description')}</p>
                            </div>
                            {offerCreated ? (
                                <div className="flex items-center gap-2 rounded-lg border bg-green-50 p-3 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-300">
                                    <Check className="h-4 w-4" /> {t('setup.offer.created')}
                                </div>
                            ) : wantOffer ? (
                                <ClubOfferForm
                                    ref={offerFormRef}
                                    storeId={storeId}
                                    hideActions
                                    onStateChange={setOfferSubmitState}
                                    onSaved={() => setOfferCreated(true)}
                                />
                            ) : (
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setWantOffer(true)}
                                        className="rounded-xl border-2 p-4 text-center transition hover:bg-accent"
                                    >
                                        <p className="font-medium">{t('setup.offer.yes')}</p>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setWantOffer(false)}
                                        className={`rounded-xl border-2 p-4 text-center transition ${wantOffer === false ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'hover:bg-accent'}`}
                                    >
                                        <p className="font-medium">{t('setup.offer.no')}</p>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {stepKey === 'funnels' && (
                        <div className="space-y-4">
                            <div>
                                <h3 className="flex items-center gap-2 font-semibold">
                                    <Rocket className="h-4 w-4" /> {t('setup.funnels.title')}
                                </h3>
                                <p className="text-muted-foreground text-sm">{t('setup.funnels.description')}</p>
                            </div>
                            {funnelScan.loading ? (
                                <div className="flex h-32 flex-col items-center justify-center gap-3 text-center">
                                    <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
                                    <p className="text-muted-foreground max-w-xs text-sm">
                                        {t('setup.funnels.scanning')}
                                    </p>
                                </div>
                            ) : funnelScan.missing.length === 0 ? (
                                <div className="flex items-center gap-2 rounded-lg border bg-green-50 p-3 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-300">
                                    <Check className="h-4 w-4" /> {t('setup.funnels.allSet')}
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {funnelScan.missing.map((f) => {
                                        const checked = selectedFunnelIds.has(f.id)
                                        return (
                                            <label
                                                key={f.id}
                                                className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 hover:bg-accent"
                                            >
                                                <Checkbox
                                                    checked={checked}
                                                    onCheckedChange={(c) =>
                                                        setSelectedFunnelIds((prev) => {
                                                            const next = new Set(prev)
                                                            if (c) next.add(f.id)
                                                            else next.delete(f.id)
                                                            return next
                                                        })
                                                    }
                                                />
                                                <span className="min-w-0 flex-1 truncate text-sm font-medium">
                                                    {f.name}
                                                </span>
                                            </label>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {stepKey === 'done' && (
                        <div className="space-y-4 py-4 text-center">
                            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-400">
                                <PartyPopper className="h-8 w-8" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold">{t('setup.done.title')}</h3>
                                <p className="text-muted-foreground mx-auto mt-1 max-w-md text-sm">
                                    {t('setup.done.description')}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter className="sm:justify-between">
                    {offerFormActive ? (
                        <>
                            <div />
                            <div className="flex gap-2">
                                <Button type="button" variant="outline" onClick={() => setWantOffer(null)}>
                                    {t('offers.modal.cancel')}
                                </Button>
                                <Button
                                    type="button"
                                    onClick={() => offerFormRef.current?.submit()}
                                    disabled={!offerSubmitState.canSubmit || offerSubmitState.isSubmitting}
                                >
                                    {offerSubmitState.isSubmitting ? t('offers.modal.creating') : t('offers.modal.submit')}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div>
                                {stepKey !== 'welcome' && stepKey !== 'done' && (
                                    <Button type="button" variant="ghost" onClick={goBack}>
                                        {t('setup.back')}
                                    </Button>
                                )}
                            </div>
                            <div className="flex gap-2">
                                {stepKey === 'done' ? (
                                    <Button type="button" onClick={finish}>
                                        {t('setup.finish')}
                                    </Button>
                                ) : (
                                    <Button type="button" onClick={goNext} disabled={!canNext}>
                                        {t('setup.next')}
                                    </Button>
                                )}
                            </div>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>

            <CreateProductDialog
                storeId={storeId}
                open={createProductOpen}
                onOpenChange={setCreateProductOpen}
                onCreated={handleProductChange}
            />
        </Dialog>
    )
}
